import type { Nullable } from 'corgi-types';
import { ChallengeType } from 'graphql-ts-types';

type Challenge = { type: ChallengeType; payload: string };

const DEFAULT_RETRY_TIMEOUT = 5000;
const DEFAULT_RETRY_INTERVAL = 250;
const TIMEOUT_ERROR_PAYLOAD = 'RecaptchaTimeoutError';

export const RECAPTCHA_SITE_KEY_SCRIPT_TYPE = 'text/recaptcha-sitekey';

export const getSiteKey = () => {
  const element = document.querySelector(`script[type="${RECAPTCHA_SITE_KEY_SCRIPT_TYPE}"]`);

  return element?.textContent?.trim() ?? null;
};

export enum ReCaptchaAction {
  RedeemGiftCard = 'redeemGiftCard',
  RegisterGuest = 'signupGuest',
  Register = 'signup',
  Login = 'login',
  AddToWishlist = 'addToWishlist',
  AddToCart = 'addToCart',
}

export const getReCaptchaChallengeToken = (action: ReCaptchaAction) =>
  new Promise<string>((resolve, reject) => {
    const siteKey = getSiteKey();

    if (siteKey === null) {
      reject(new Error('ReCaptcha site key not available'));
    }

    window.grecaptcha.enterprise.ready(() => {
      window.grecaptcha.enterprise.execute(siteKey, { action }).then(
        token => {
          resolve(token);
        },
        error => {
          reject(error);
        }
      );
    });
  });

export const getReCaptchaChallengeWithRetry = async (
  action: ReCaptchaAction,
  timeout = DEFAULT_RETRY_TIMEOUT,
  throttle = DEFAULT_RETRY_INTERVAL
): Promise<Challenge> => {
  let challenge: Nullable<Challenge> = null;

  const startTime = Date.now();
  while (Date.now() - startTime < timeout) {
    try {
      challenge = await getReCaptchaChallenge(action);
    } catch {
      // eslint-disable-next-line no-console
      console.warn("'grecaptcha' object was not loaded yet. Retrying...");
    }

    if (challenge?.payload) {
      break;
    }

    await new Promise(resolve => setTimeout(resolve, throttle));
  }

  return challenge?.payload
    ? challenge
    : { type: ChallengeType.ReCaptcha, payload: TIMEOUT_ERROR_PAYLOAD };
};

export const getReCaptchaChallenge = async (action: ReCaptchaAction): Promise<Challenge> => {
  const token = await getReCaptchaChallengeToken(action);

  return {
    type: ChallengeType.ReCaptcha,
    payload: token,
  };
};
